(() => {
  if (customElements.get('elteres-sticky-cart')) return;
  class ElteresStickyCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.mobile = matchMedia('(max-width: 767px)');
      this.onMedia = () => this.mobile.matches ? this.start() : this.stop();
      this.mobile.addEventListener('change', this.onMedia);
      this.onMedia();
    }
    start() {
      if (this.running) return;
      this.root = document.getElementById('ProductSection-' + this.dataset.sectionId);
      this.details = this.root?.querySelector('product-details');
      if (!this.details) return;
      this.running = true;
      this.chosen = this.chosen || new Set();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      document.body.appendChild(this);
      this.spacer = document.createElement('div');
      this.spacer.className = 'elteres-sticky-cart__spacer';
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(this.spacer);
      this.button = this.querySelector('[data-sticky-add]');
      this.price = this.querySelector('[data-sticky-price]');
      this.option = this.querySelector('[data-sticky-option]');
      this.schedule = () => {
        if (!this.frame && this.running) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.sync();
        });
      };
      this.button.addEventListener('click', () => this.submit(), {signal});
      this.details.addEventListener('click', e => this.recordChoice(e), {capture:true,signal});
      this.details.addEventListener('change', e => this.recordChoice(e), {capture:true,signal});
      window.addEventListener('scroll', this.schedule, {passive:true,signal});
      window.addEventListener('resize', this.schedule, {passive:true,signal});
      window.visualViewport?.addEventListener('resize', this.schedule, {passive:true,signal});
      document.addEventListener('focusin', this.schedule, {signal});
      document.addEventListener('focusout', this.schedule, {signal});
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.details, {childList:true,subtree:true,attributes:true,attributeFilter:['disabled','class','value','style']});
      this.overlayObserver = new MutationObserver(this.schedule);
      this.overlayObserver.observe(document.body, {childList:true,attributes:true,attributeFilter:['class','style']});
      this.overlays = new MutationObserver(this.schedule);
      document.querySelectorAll('cart-drawer,size-guide-drawer,search-dropdown,#NavDrawer,#shopify-pc__banner').forEach(el =>
        this.overlays.observe(el,{attributes:true,attributeFilter:['aria-hidden','class','style','open']}));
      this.onChanging = context => {
        if (context.sectionId !== this.dataset.sectionId) return;
        this.pending = true;
        this.expected = context.variant?.id ? String(context.variant.id) : null;
        this.schedule();
      };
      this.onUpdated = context => {
        if (context.sectionId !== this.dataset.sectionId) return;
        this.pending = false;
        this.schedule();
      };
      this.onAdded = context => {
        if (context.sectionId !== this.dataset.sectionId) return;
        this.lockUntil = Date.now() + 650;
        this.schedule();
        clearTimeout(this.unlockTimer);
        this.unlockTimer = setTimeout(this.schedule, 700);
      };
      this.bus = window.eventBus;
      this.bus?.on('variant:change', this.onChanging);
      this.bus?.on('variant:updated', this.onUpdated);
      this.bus?.on('cart:added', this.onAdded);
      this.sync();
    }
    stop() {
      if (!this.running) return;
      this.running = false;
      this.hidden = true;
      this.abort?.abort();
      this.observer?.disconnect();
      this.overlayObserver?.disconnect();
      this.overlays?.disconnect();
      this.intersection?.disconnect();
      this.bus?.off('variant:change', this.onChanging);
      this.bus?.off('variant:updated', this.onUpdated);
      this.bus?.off('cart:added', this.onAdded);
      this.spacer?.remove();
      this.prompt?.remove();
      this.details?.querySelectorAll('.elteres-sticky-cart__needs-selection').forEach(el=>el.classList.remove('elteres-sticky-cart__needs-selection'));
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      clearTimeout(this.unlockTimer);
    }
    groups() {
      return Array.from(this.details.querySelectorAll('product-swatch,variant-dropdown')).filter(group => {
        const label = group.querySelector('.option-label-wrapper')?.textContent || '';
        return /beden|boyut|size|taille|größe|talla/i.test(label);
      });
    }
    recordChoice(event) {
      let item = event.target.closest('[data-product-variant]');
      const label = event.target.closest('label[for]');
      if (!item && label) item = this.details.querySelector('[id="' + CSS.escape(label.htmlFor) + '"]');
      const group = item?.closest('product-swatch,variant-dropdown');
      if (!group) return;
      this.chosen.add(group.dataset.optionIndex);
      group.classList.remove('elteres-sticky-cart__needs-selection');
      this.prompt?.remove();
      this.schedule();
    }
    isVisible(el) {
      if (!el || el.hidden || el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return false;
      const r = el.getBoundingClientRect(), s = getComputedStyle(el);
      return s.display !== 'none' && s.visibility !== 'hidden' && Number(s.opacity) !== 0 && r.width > 0 && r.height > 0 && r.right > 1 && r.left < innerWidth - 1 && r.bottom > 0 && r.top < innerHeight;
    }
    obstacles() {
      let blocked = false, offset = 0;
      const selectors = 'dialog[open],[role="dialog"],#NavDrawer,#shopify-pc__banner,[id*="cookie" i],[class*="cookie-banner" i],[id*="chat" i],iframe,nav,[data-mobile-bottom-menu]';
      document.querySelectorAll(selectors).forEach(el => {
        if (el === this || this.contains(el) || !this.isVisible(el)) return;
        if (el.matches('[role="dialog"],dialog,#NavDrawer') || /cookie|shopify-pc__banner/i.test(el.id + ' ' + el.className)) {
          blocked = true;
          return;
        }
        const r = el.getBoundingClientRect(), s = getComputedStyle(el);
        if (s.position !== 'fixed' || r.bottom < innerHeight - 100 || r.top < innerHeight / 2) return;
        offset = Math.max(offset, innerHeight - r.top + 8);
      });
      const active = document.activeElement;
      const keyboard = active?.matches('textarea,input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"])') &&
        window.visualViewport && window.visualViewport.height < innerHeight - 120;
      return {blocked:blocked || keyboard,offset};
    }
    setText(el, value) {
      if (el.textContent !== value) el.textContent = value;
    }
    sync() {
      if (!this.running || !this.root.isConnected) {
        this.stop();
        return;
      }
      this.formComponent = this.details.querySelector('product-form');
      this.form = this.formComponent?.querySelector('form');
      const main = this.form?.querySelector('[name="add"]');
      if (!main) {
        this.hidden = true;
        return;
      }
      if (this.main !== main) {
        this.main = main;
        this.intersection?.disconnect();
        this.intersection = new IntersectionObserver(this.schedule,{threshold:[0,1]});
        this.intersection.observe(main);
      }
      const idInput = this.form.querySelector('[name="id"]');
      const id = idInput?.value;
      if (this.pending && this.expected && id === this.expected) this.pending = false;
      if (this.pending && !this.expected && main.disabled) this.pending = false;
      const groups = this.groups();
      this.missing = groups.find(g=>!this.chosen.has(g.dataset.optionIndex));
      const selected = groups.map(g=>{
        const value = g.querySelector('input[data-product-variant]:checked')?.value ||
          g.querySelector('li[data-product-variant][selected]')?.dataset.optionValue;
        const label = g.querySelector('.option-label-wrapper .header,.option-label-wrapper label')?.textContent.trim() || '';
        return value ? label + ': ' + value : '';
      }).filter(Boolean).join(' · ');
      const loading = main.querySelector('[data-loading-icon]');
      const busy = Boolean(this.formComponent.isProcessing || (loading && !loading.classList.contains('hidden')) || Date.now() < (this.lockUntil || 0));
      const unavailable = idInput?.disabled || !id || (main.disabled && !busy && !this.pending);
      const currentPrice = main.querySelector('.product-price-on-button')?.textContent.trim() ||
        this.details.querySelector('[data-product-price] [data-price-text]')?.textContent.trim() || '';
      this.setText(this.price,currentPrice);
      this.setText(this.option,this.missing ? this.dataset.chooseLabel : selected);
      const mainLabel = main.querySelector('[data-add-to-cart-text]')?.textContent.trim();
      this.setText(this.button,busy || this.pending ? this.dataset.loadingLabel :
        unavailable ? mainLabel || this.dataset.soldOutLabel :
        this.missing ? this.dataset.chooseLabel : this.dataset.addLabel);
      this.button.disabled = Boolean(busy || this.pending || unavailable);
      this.button.setAttribute('aria-busy', String(Boolean(busy || this.pending)));
      this.button.setAttribute('aria-label', [this.button.textContent,this.option.textContent,currentPrice].filter(Boolean).join(' — '));
      const r = main.getBoundingClientRect();
      const {blocked,offset} = this.obstacles();
      this.style.setProperty('--elteres-sticky-offset',offset + 'px');
      this.hidden = !(r.bottom <= 0 && !blocked && this.mobile.matches);
      if (!this.hidden) {
        const height = Math.ceil(this.getBoundingClientRect().height);
        const reserved = height + offset;
        this.spacer.style.height = reserved + 'px';
        if (document.body.style.getPropertyValue('--elteres-sticky-height') !== reserved + 'px') document.body.style.setProperty('--elteres-sticky-height',reserved + 'px');
      }
    }
    submit() {
      this.sync();
      if (this.button.disabled || !this.form || !this.main) return;
      if (this.missing) {
        const group = this.missing;
        group.classList.add('elteres-sticky-cart__needs-selection');
        this.prompt?.remove();
        this.prompt = document.createElement('p');
        this.prompt.className = 'elteres-sticky-cart__prompt';
        this.prompt.setAttribute('role','status');
        this.prompt.textContent = this.dataset.chooseLabel;
        group.prepend(this.prompt);
        group.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'center'});
        const control = group.querySelector('input[data-product-variant]:checked,[data-dropdown-button],input[data-product-variant]');
        control?.focus({preventScroll:true});
        return;
      }
      if (!customElements.get('product-form') || this.formComponent.isProcessing) return;
      this.lockUntil = Date.now() + 650;
      this.button.disabled = true;
      this.main.click();
      this.schedule();
      clearTimeout(this.unlockTimer);
      this.unlockTimer = setTimeout(this.schedule,700);
    }
  }
  customElements.define('elteres-sticky-cart',ElteresStickyCart);
})();
